import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";

const PAGE_SIZE = 10;

export default function HrLeaves() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/hr/leaves");
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load leaves");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const query = q.toLowerCase();
    return rows.filter((r) => {
      const name = `${r.first_name} ${r.last_name}`.toLowerCase();
      const matchQ =
        !query ||
        name.includes(query) ||
        r.employee_code?.toLowerCase().includes(query) ||
        r.leave_type?.toLowerCase().includes(query);

      const matchStatus =
        status === "all" ? true : r.status === status;

      return matchQ && matchStatus;
    });
  }, [rows, q, status]);

  const totalPages = Math.max(Math.ceil(filtered.length / PAGE_SIZE), 1);
  const pageData = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const updateStatus = async (id, status) => {
    if (!window.confirm(`Mark leave as ${status}?`)) return;
    await api.put(`/hr/leaves/${id}/status`, { status });
    load();
  };

  return (
    <HrLayout title="Leave Requests">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-800">Leave Requests</h1>
        <p className="text-sm text-slate-500">
          Approve or reject employee leave applications
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search employee / leave type"
          className="border rounded-lg px-3 py-2 text-sm"
        />

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100">
            <tr>
              <Th>Employee</Th>
              <Th>Department</Th>
              <Th>Type</Th>
              <Th>From</Th>
              <Th>To</Th>
              <Th>Status</Th>
              <Th className="text-right">Action</Th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <Td colSpan={7} className="text-center py-8">
                  Loading...
                </Td>
              </tr>
            ) : pageData.length === 0 ? (
              <tr>
                <Td colSpan={7} className="text-center py-8 text-slate-500">
                  No leave requests
                </Td>
              </tr>
            ) : (
              pageData.map((l) => (
                <tr key={l.id} className="border-t">
                  <Td>
                    <b>{l.first_name} {l.last_name}</b>
                    <div className="text-xs text-slate-500">
                      {l.employee_code}
                    </div>
                  </Td>
                  <Td>{l.department_name || "—"}</Td>
                  <Td>{l.leave_type}</Td>
                  <Td>{l.from_date}</Td>
                  <Td>{l.to_date}</Td>
                  <Td>
                    <StatusChip status={l.status} />
                  </Td>
                  <Td className="text-right">
                    {l.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => updateStatus(l.id, "approved")}
                          className="px-3 py-1 rounded bg-emerald-600 text-white text-xs"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateStatus(l.id, "rejected")}
                          className="px-3 py-1 rounded bg-rose-600 text-white text-xs"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </HrLayout>
  );
}

/* helpers */
function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-bold text-slate-600 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, colSpan, className = "" }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>
      {children}
    </td>
  );
}
function StatusChip({ status }) {
  const map = {
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${map[status]}`}>
      {status.toUpperCase()}
    </span>
  );
}
